<template>
  <form
    novalidate
    id="agreement_form"
    class="form"
    @submit.prevent="submit">
    <Alert v-if="formHasErrors" type="error" @close="clearFormErrors">
      <div v-for="error in errors.items" :key="error.id">
        {{error.msg}}
      </div>
    </Alert>

    <v-layout row wrap>
      <v-flex
        v-for="agreement in model"
        :key="`agreement_${agreement.id}`"
        xs12
        class="agreement-row">
        <FormRecord :agreement="agreement" />
      </v-flex>
    </v-layout>

    <v-layout row wrap class="submit">
      <v-flex xs12>
        <button
          :disabled="disableSave"
          class="btn-primary btn-submit test-sign-agreements"
          type="submit">
          {{saveButtonLabel}}
        </button>
      </v-flex>
    </v-layout>
  </form>
</template>

<script>
import { Mixins } from '@spectorasoftware/homegauge-vue';
import { mapState } from 'vuex';

import Alert from '@/components/Shared/Alert.vue';
import FormRecord from './FormRecord.vue';

const { FormValidationsMixin } = Mixins;

export default {
  $_veeValidate: {
    validator: 'agreements',
  },
  components: {
    Alert,
    FormRecord,
  },
  mixins: [FormValidationsMixin],
  provide() {
    return {
      $validator: this.$validator,
    };
  },
  props: {
    agreements: {
      type: Array,
      required: true,
    },
    folderId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      model: [],
    };
  },
  computed: {
    ...mapState('Inspection', {
      loading: state => state.loading,
    }),
    agreementHasChangedStatus() {
      return this.model.filter(agreement => agreement.status
        && (agreement.status !== agreement.newStatus)).length > 0;
    },
    allSigned() {
      return this.agreements.reduce((allSigned, current) => allSigned && (current.status === 'SIGNED'), true);
    },
    disableSave() {
      return this.loading || !this.agreementHasChangedStatus || this.allSigned;
    },
    saveButtonLabel() {
      const plurality = this.model.length > 1 ? 'Agreements' : 'Agreement';
      return this.loading ? `Saving ${plurality}` : `Sign ${plurality}`;
    },
  },
  mounted() {
    this.model = this.agreements.map(agreement => Object.assign(
      {},
      agreement,
      {
        newStatus: agreement.status !== 'UNSIGNED' ? agreement.status : null,
      },
    ));
  },
  methods: {
    async submit() {
      // early return if currently loading - prevent double submission
      if (this.loading) {
        return;
      }

      const result = await this.$validator.validateAll();
      // early return if there are validation errors
      if (!result) {
        this.$scrollTo('.error');
        return;
      }

      const agreements = this.model
        .filter(agreement => agreement.newStatus !== agreement.status)
        .map(agreement => {
          const output = {
            ...agreement,
            status: agreement.newStatus ? agreement.newStatus : '',
          };
          delete output.newStatus;
          return output;
        });

      await this.$store.dispatch('Folders/saveAgreements', {
        agreements,
        folderId: this.folderId,
      });
      this.$emit('saved', agreements);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';

$_margin: 60px;

p {
  color: rgba($theme-color-black, 0.6);
  font-size: 1.286rem;
  margin: 0;
  padding: 0;
}

.agreement-row {
  ~ .agreement-row {
    margin-top: $_margin * 2.5;
  }
}

.submit {
  margin-bottom: $_margin;
  margin-top: $_margin;
  text-align: center;
}

button:disabled {
  background-color: lighten(desaturate(adjust-hue($theme-color-primary, 2), 26.67), 19.41);
}

.form ::v-deep .v-alert {
  margin-bottom: $_margin !important;
}
</style>
